import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQueries, useQuery } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { Copy, Download, ExternalLink, Languages, Loader2, QrCode, ScrollText, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { BadgeToggleGroup } from '@/components/BadgeToggleGroup'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { warningText } from '@/components/ValidationWarnings'
import { CopyableCode } from '@/components/CopyableCode'
import { copyToClipboard } from '@/utils/clipboard'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  TRANSPARENCY_LANGUAGES,
  TRANSPARENCY_LANGUAGE_NAMES,
  audioDisclaimerScript,
  campaignTransparencyUrl,
  transparencyLanguageFor,
  type TransparencyLanguage,
} from '@/services/political/transparencyNotice'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import { PoliticalEntitiesApi } from '@/services/political/politicalEntitiesApi'
import type { AdvertiserPoliticalCampaignRef } from '@/services/advertisers/advertisersApi'
import type { PoliticalCampaign, PoliticalCampaignTopic } from '@/types/models'

interface CampaignTransparencyProps {
  /** The campaign's publicId — what the notice page resolves the campaign by. */
  publicId: string
  sponsorName: string
  electionName?: string
  /** The notice is generated for one topic, which sets the payer and budget it shows. */
  topics: PoliticalCampaignTopic[]
  /** File name stem for QR code downloads. */
  fileName?: string
  /** Set by a parent that picks the language for several notices at once; hides the picker. */
  language?: TransparencyLanguage
  /**
   * A look at the notice rather than a choice for an ad: topics show as badges
   * and the first is picked up front, so the notice is visible straight away.
   */
  preview?: boolean
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',')
  const type = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream'
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
  return new Blob([bytes], { type })
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Everything an advertiser needs to disclose a political campaign: the link to
 * its public transparency notice, a printable QR code for that link, and the
 * script an audio ad must read out. One language drives all three.
 */
export function CampaignTransparency({
  publicId,
  sponsorName,
  electionName,
  topics,
  fileName,
  language,
  preview,
}: CampaignTransparencyProps) {
  const { t, i18n } = useTranslation()
  const [ownLang, setLang] = useState<TransparencyLanguage>(() => transparencyLanguageFor(i18n.language))
  const lang = language ?? ownLang
  // A lone topic needs no choosing; otherwise the advertiser must say which one the ad is about.
  const [topicId, setTopicId] = useState(() =>
    topics.length === 1 || (preview && topics.length > 0) ? topics[0].id : ''
  )
  const [qrOpen, setQrOpen] = useState(false)

  if (topics.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('political.transparency.noTopics')}</p>
  }
  const topic = topics.find((tp) => tp.id === topicId)
  const url = topic ? campaignTransparencyUrl(publicId, topic.id, lang) : null
  const script = audioDisclaimerScript(lang, { sponsorName, electionName })

  return (
    <div className="flex flex-col gap-4">
      {preview ? (
        topics.length > 1 && (
          <BadgeToggleGroup
            label={t('political.transparency.topic')}
            options={topics.map((tp) => ({ label: tp.name, value: tp.id }))}
            value={[topicId]}
            // Single choice: clicking the picked topic again keeps it picked.
            onChange={(ids) => setTopicId(ids.find((id) => id !== topicId) ?? topicId)}
          />
        )
      ) : (
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-2">
          <Label>{t('political.transparency.topic')}</Label>
          <Select
            label={t('political.transparency.topic')}
            value={topicId}
            onValueChange={(v) => v && setTopicId(v)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t('political.topicPlaceholder')}>{topic?.name ?? null}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {topics.map((tp) => (
                <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!language && (
        <div className="flex flex-col gap-2">
          <Label>{t('political.transparency.language')}</Label>
          <Select
            label={t('political.transparency.language')}
            value={lang}
            onValueChange={(v) => v && setLang(v as TransparencyLanguage)}
          >
            <SelectTrigger className="w-48">
              <SelectValue>{TRANSPARENCY_LANGUAGE_NAMES[lang]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TRANSPARENCY_LANGUAGES.map((l) => (
                <SelectItem key={l} value={l}>{TRANSPARENCY_LANGUAGE_NAMES[l]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
      </div>
      )}

      {!topic ? (
        <p className="text-sm text-muted-foreground">{t('political.transparency.selectTopic')}</p>
      ) : !url ? null : (
        <>
        <div className="flex flex-col gap-2">
          <Label>{t('political.transparency.link')}</Label>
          <CopyableCode code={url} copyLabel={t('political.transparency.copyLink')} />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" asChild>
              <a href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="size-4" />
                {t('political.transparency.openNotice')}
              </a>
            </Button>
            <Button type="button" variant="outline" onClick={() => setQrOpen(true)}>
              <QrCode className="size-4" />
              {t('political.transparency.qrCode')}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>{t('political.transparency.audioScript')}</Label>
          <div className="flex items-start gap-3">
            <blockquote lang={lang} className="min-w-0 flex-1 rounded-md border bg-muted/50 p-3 text-sm">
              {script}
            </blockquote>
            <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => copyToClipboard(script)}>
              <Copy className="size-4" />
              {t('political.transparency.copyScript')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('political.transparency.audioScriptHint')}</p>
        </div>

        <TransparencyQrDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          url={url}
          fileName={`${fileName ?? 'transparency-qr'}-${topic.name}`}
        />
        </>
      )}
    </div>
  )
}

interface TransparencyQrDialogProps {
  open: boolean
  onClose: () => void
  url: string
  fileName: string
}

function TransparencyQrDialog({ open, onClose, url, fileName }: TransparencyQrDialogProps) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['transparencyQr', url],
    // PNG for screens and quick use; SVG scales cleanly for print.
    queryFn: async () => ({
      png: await QRCode.toDataURL(url, { width: 1024, margin: 2 }),
      svg: await QRCode.toString(url, { type: 'svg', margin: 2 }),
    }),
    enabled: open,
    staleTime: Infinity,
  })

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            <QrCode className="size-4" />
            {t('political.transparency.qrCode')}
          </DialogTitle>
          <DialogDescription>{t('political.transparency.qrDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex justify-center py-2">
          {isLoading && <Loader2 className="size-6 animate-spin text-muted-foreground" />}
          {isError && <p className="text-sm text-destructive">{t('political.transparency.qrFailed')}</p>}
          {data && (
            <img
              src={data.png}
              alt={t('political.transparency.qrAlt')}
              className="size-52"
            />
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={!data}
            onClick={() => data && downloadBlob(new Blob([data.svg], { type: 'image/svg+xml' }), `${fileName}.svg`)}
          >
            <Download className="size-4" />
            SVG
          </Button>
          <Button
            type="button"
            disabled={!data}
            onClick={() => data && downloadBlob(dataUrlToBlob(data.png), `${fileName}.png`)}
          >
            <Download className="size-4" />
            PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** `preview` labels the card as a look at the notice rather than a setting. */
function TransparencyCardHeader({ preview, action }: { preview?: boolean; action?: React.ReactNode }) {
  const { t } = useTranslation()
  return (
    <CardHeader>
      <div>
        <CardTitle>
          <CardIcon icon={ScrollText} />
          {t(preview ? 'political.transparency.previewTitle' : 'political.transparency.title')}
        </CardTitle>
        <div className="ad-card__subtitle">
          {t(preview ? 'political.transparency.previewSubtitle' : 'political.transparency.subtitle')}
        </div>
      </div>
      {action && <CardAction>{action}</CardAction>}
    </CardHeader>
  )
}

/**
 * The transparency card for a single campaign, as the political module shows
 * it. The public notice only resolves a campaign that has no validation
 * warnings and whose sponsor is STANDARD and has none either
 * (`PublicPoliticalCampaignResource.isPubliclyVisible` in core), so until then
 * the card says which of those is in the way instead of offering a dead link.
 */
export function CampaignTransparencyCard({ campaign, className }: { campaign: PoliticalCampaign; className?: string }) {
  const { t } = useTranslation()
  const { data: sponsor, isLoading } = useQuery({
    queryKey: ['politicalEntity', campaign.politicalEntityId],
    queryFn: () => PoliticalEntitiesApi.getById(campaign.politicalEntityId),
    enabled: !!campaign.publicId && !!campaign.politicalEntityId,
  })
  if (!campaign.publicId) return null

  const sponsorWarnings = sponsor?.validationWarnings ?? []
  const sponsorHidden = !!sponsor?.visibilityType && sponsor.visibilityType !== 'STANDARD'
  const blocked = campaign.validationWarnings.length > 0 || sponsorWarnings.length > 0 || sponsorHidden
  const sponsorLink = sponsor && (
    <Link to={`/political/entities/${sponsor.id}`} className="font-medium underline underline-offset-2">
      {sponsor.name}
    </Link>
  )

  return (
    <Card tone="attention" className={className}>
      <TransparencyCardHeader />
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full rounded-md" />
        ) : blocked ? (
          <Alert variant="warning">
            <TriangleAlert />
            <AlertTitle>{t('political.transparency.blockedTitle')}</AlertTitle>
            <AlertDescription>
              <ul className="list-disc space-y-1 pl-4">
                {campaign.validationWarnings.length > 0 && (
                  <li>{t('political.transparency.blockedCampaignWarnings')}</li>
                )}
                {sponsorWarnings.length > 0 && (
                  <li>
                    {t('political.transparency.blockedSponsorWarnings')} {sponsorLink}
                    <ul className="list-[circle] pl-4">
                      {sponsorWarnings.map((w, i) => (
                        <li key={`${w.code}-${i}`}>{warningText(w)}</li>
                      ))}
                    </ul>
                  </li>
                )}
                {sponsorHidden && (
                  <li>
                    {t('political.transparency.blockedSponsorVisibility', {
                      visibility: t(`political.visibilityTypes.${sponsor!.visibilityType}`),
                    })}{' '}
                    {sponsorLink}
                  </li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        ) : (
          <CampaignTransparency
            publicId={campaign.publicId}
            sponsorName={campaign.politicalEntityName}
            electionName={campaign.electionName}
            topics={campaign.topics}
            fileName={`transparency-${campaign.id}`}
          />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * An advertiser's linked campaigns as the public endpoint returns them, one
 * query per campaign in the same order. A campaign whose notice is not ready
 * comes back as null.
 */
export function useLinkedPublicCampaigns(campaigns: AdvertiserPoliticalCampaignRef[]) {
  return useQueries({
    queries: campaigns.map((ref) => ({
      queryKey: ['publicCampaign', ref.publicId],
      queryFn: () => PoliticalCampaignsApi.getPublicById(ref.publicId),
    })),
  })
}

/** The sponsors behind an advertiser's linked campaigns, as one comma-separated name. */
export function useLinkedSponsorNames(campaigns: AdvertiserPoliticalCampaignRef[]): string {
  const results = useLinkedPublicCampaigns(campaigns)
  return [
    ...new Set(results.map((q) => q.data?.politicalEntityName).filter((n): n is string => !!n)),
  ].join(', ')
}

/**
 * The transparency card for an advertiser's linked campaigns. Those usually
 * live on the political network, so they are read through the anonymous
 * public endpoint — which also only returns campaigns whose notice is ready.
 */
export function AdvertiserTransparencyCard({ campaigns }: { campaigns: AdvertiserPoliticalCampaignRef[] }) {
  const { t, i18n } = useTranslation()
  const [lang, setLang] = useState<TransparencyLanguage>(() => transparencyLanguageFor(i18n.language))
  const results = useLinkedPublicCampaigns(campaigns)
  if (campaigns.length === 0) return null

  return (
    <Card tone="attention">
      <TransparencyCardHeader
        preview
        action={
          <Select
            label={t('political.transparency.language')}
            value={lang}
            onValueChange={(v) => v && setLang(v as TransparencyLanguage)}
          >
            <SelectTrigger className="w-40" aria-label={t('political.transparency.language')}>
              <Languages className="size-4 text-muted-foreground" />
              <SelectValue>{TRANSPARENCY_LANGUAGE_NAMES[lang]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TRANSPARENCY_LANGUAGES.map((l) => (
                <SelectItem key={l} value={l}>{TRANSPARENCY_LANGUAGE_NAMES[l]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <CardContent>
        <Accordion type="multiple">
          {campaigns.map((ref, i) => {
            const { data: campaign, isLoading, isError } = results[i]
            const details = [campaign?.politicalEntityName, campaign?.electionName].filter(Boolean)
            const topicCount = campaign?.topics?.length ?? 0
            return (
              <AccordionItem key={ref.publicId} value={ref.publicId}>
                <AccordionTrigger className="items-center hover:no-underline">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate font-semibold">{campaign?.name || ref.name}</span>
                    {details.length > 0 && (
                      <span className="truncate text-xs font-normal text-muted-foreground">
                        {details.join(' · ')}
                      </span>
                    )}
                  </div>
                  {isLoading ? (
                    <Skeleton className="h-5 w-16 rounded-full" />
                  ) : isError ? (
                    <Badge variant="red">{t('political.transparency.loadFailedBadge')}</Badge>
                  ) : campaign ? (
                    <Badge variant="success">
                      {t('political.transparency.topicCount', { count: topicCount })}
                    </Badge>
                  ) : (
                    <Badge variant="gray">{t('political.transparency.notPublicBadge')}</Badge>
                  )}
                </AccordionTrigger>
                <AccordionContent>
                  {isLoading ? (
                    <Skeleton className="h-24 w-full rounded-md" />
                  ) : isError ? (
                    <p className="text-sm text-destructive">{t('political.transparency.loadFailed')}</p>
                  ) : campaign ? (
                    <CampaignTransparency
                      publicId={ref.publicId}
                      sponsorName={campaign.politicalEntityName}
                      electionName={campaign.electionName}
                      topics={campaign.topics ?? []}
                      fileName={`transparency-${ref.id}`}
                      language={lang}
                      preview
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('political.transparency.notPublic')}</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CardContent>
    </Card>
  )
}
